import { useCallback, useMemo, useState } from 'react';
import type { MouseEvent } from 'react';
import { MoreVert, PictureAsPdfOutlined, PlaylistAddCheckOutlined, TableChartOutlined } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { firmValuationsUrl } from 'common/config/urls';
import { BULK_ACTIONS, EXCEL_EXPORT, PDF_EXPORT } from 'common/constants/pageActions';
import { useExtraPageActionVisibility } from 'common/hooks';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { PdfExportConfigDialog } from 'components/PdfExportConfigDialog';
import { PrimaryNavigationMenu, PrimaryNavigationMenuItem } from 'components/ui';
import { useSecondaryNavigationRegistration } from 'context/FirmBaseLayoutContext';
import { SummaryDefinitionsProvider } from 'context/SummaryDefinitionsContext';
import BulkActionsDrawer from 'layouts/Main/components/BulkActionsLedger/BulkActionsDrawer';
import { ExcelExportDialogV2 } from 'layouts/Main/components/ExcelExportDialogV2';
import { FundDialogForm } from 'pages/Funds/forms';
import FirmSummaryFundFilterMenu from 'pages/Intelligence/FirmSummaryFundFilterMenu';
import FirmSummaryWorkspace from 'pages/Intelligence/FirmSummaryWorkspace';
import { IntelligenceProvider } from 'pages/Intelligence/IntelligenceContext';
import useFirmSummaryFundFilter from 'pages/Intelligence/useFirmSummaryFundFilter';
import { useDialogFormsStore } from 'store';
import { getObjectValue } from 'utilities';
import { useUserUtils } from 'utilities/userUtils';
import {
  VALUATIONS_DEFAULT_TERTIARY_NAVIGATION_SLUG,
  VALUATIONS_TERTIARY_NAVIGATION_LABELS,
  VALUATIONS_TERTIARY_NAVIGATION_SLUGS,
} from './firmValuationsNavigation.constants';

type FirmValuationsRouteParams = {
  firmSlugParam: string;
  tertiaryTabSlugParam: string;
};

interface FirmValuationsContentProps {
  firmSlug: string;
  activeTertiaryTabSlug: string;
}

const MORE_VERT_ICON = <MoreVert />;

const FirmValuationsContent = ({ firmSlug, activeTertiaryTabSlug }: FirmValuationsContentProps) => {
  const { titleAccessory, ...menuProps } = useFirmSummaryFundFilter();
  const displayFundDialogForm = useDialogFormsStore(state => state.displayFundDialogForm);
  const setDisplayFundDialogForm = useDialogFormsStore(state => state.setDisplayFundDialogForm);
  const handleCloseFundDialog = useCallback(() => setDisplayFundDialogForm(false), [setDisplayFundDialogForm]);

  const { isStaffUser, isSuperUser } = useUserUtils();
  const actionVisibility = useExtraPageActionVisibility({
    pdfExport: isStaffUser() || isSuperUser(),
  });

  const [moreActionsAnchorEl, setMoreActionsAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [excelExportOpen, setExcelExportOpen] = useState(false);
  const [bulkActionsOpen, setBulkActionsOpen] = useState(false);
  const [pdfExportOpen, setPdfExportOpen] = useState(false);

  const handleMoreActionsOpen = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setMoreActionsAnchorEl(event.currentTarget);
  }, []);

  const handleMoreActionsClose = useCallback(() => {
    setMoreActionsAnchorEl(null);
  }, []);

  const handleExcelExportClick = useCallback(() => {
    handleMoreActionsClose();
    setExcelExportOpen(true);
  }, [handleMoreActionsClose]);

  const handleBulkActionsClick = useCallback(() => {
    handleMoreActionsClose();
    setBulkActionsOpen(true);
  }, [handleMoreActionsClose]);

  const handlePdfExportClick = useCallback(() => {
    handleMoreActionsClose();
    setPdfExportOpen(true);
  }, [handleMoreActionsClose]);

  const hasMoreActions = actionVisibility.excelExport || actionVisibility.bulkActions || actionVisibility.pdfExport;

  const secondaryNavigation = useMemo(
    () => ({
      title: 'Valuations',
      titleAccessory,
      actions: hasMoreActions
        ? [
          {
            ariaLabel: 'More actions',
            icon: MORE_VERT_ICON,
            onClick: handleMoreActionsOpen,
            type: 'iconButton' as const,
          },
        ]
        : [],
    }),
    [hasMoreActions, handleMoreActionsOpen, titleAccessory]
  );

  useSecondaryNavigationRegistration(secondaryNavigation);

  return (
    <>
      <FirmSummaryWorkspace
        firmSlug={firmSlug}
        activeTertiaryTabSlug={activeTertiaryTabSlug}
        baseUrlFn={firmValuationsUrl}
        navigationSlugs={VALUATIONS_TERTIARY_NAVIGATION_SLUGS}
        navigationLabels={VALUATIONS_TERTIARY_NAVIGATION_LABELS}
        navigationAriaLabel="Valuations views"
        summaryType="valuations"
      />
      <FirmSummaryFundFilterMenu {...menuProps} />
      <FundDialogForm open={displayFundDialogForm} handleOnClose={handleCloseFundDialog} onSuccess={handleCloseFundDialog} />
      <PrimaryNavigationMenu
        anchorEl={moreActionsAnchorEl}
        menuMinWidth={0}
        menuWidth="content"
        onClose={handleMoreActionsClose}
        open={Boolean(moreActionsAnchorEl)}>
        {actionVisibility.excelExport && (
          <PrimaryNavigationMenuItem icon={<TableChartOutlined fontSize="inherit" />} onClick={handleExcelExportClick}>
            {EXCEL_EXPORT}
          </PrimaryNavigationMenuItem>
        )}
        {actionVisibility.bulkActions && (
          <PrimaryNavigationMenuItem icon={<PlaylistAddCheckOutlined fontSize="inherit" />} onClick={handleBulkActionsClick}>
            {BULK_ACTIONS}
          </PrimaryNavigationMenuItem>
        )}
        {actionVisibility.pdfExport && (
          <PrimaryNavigationMenuItem icon={<PictureAsPdfOutlined fontSize="inherit" />} onClick={handlePdfExportClick}>
            {PDF_EXPORT}
          </PrimaryNavigationMenuItem>
        )}
      </PrimaryNavigationMenu>
      <ExcelExportDialogV2 open={excelExportOpen} onClose={() => setExcelExportOpen(false)} />
      <BulkActionsDrawer open={bulkActionsOpen} onClose={() => setBulkActionsOpen(false)} />
      <PdfExportConfigDialog open={pdfExportOpen} onClose={() => setPdfExportOpen(false)} />
    </>
  );
};

const FirmValuations = () => {
  const { firmSlugParam, tertiaryTabSlugParam } = useParams<FirmValuationsRouteParams>();
  const [storeValue] = useStore() as UseStoreValues;
  const { firmInfo } = getObjectValue(storeValue);
  const firmId = getObjectValue(firmInfo).id as number | undefined;

  if (!firmSlugParam || !firmId) {
    return null;
  }

  const activeTertiaryTabSlug = tertiaryTabSlugParam ?? VALUATIONS_DEFAULT_TERTIARY_NAVIGATION_SLUG;

  return (
    <SummaryDefinitionsProvider entityId={firmId} summaryType="valuations" fetchEnabled>
      <IntelligenceProvider>
        <FirmValuationsContent firmSlug={firmSlugParam} activeTertiaryTabSlug={activeTertiaryTabSlug} />
      </IntelligenceProvider>
    </SummaryDefinitionsProvider>
  );
};

export default FirmValuations;
